import {
  type AuthError,
  createUserWithEmailAndPassword,
  type User as FirebaseUser,
  getIdToken,
  getMultiFactorResolver,
  linkWithCredential,
  type MultiFactorError,
  OAuthProvider,
  onAuthStateChanged,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import React, {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import MfaChallengeDialog from '../components/Dialogs/MfaChallengeDialog';
import { useAccountLinking } from '../hooks/useAccountLinking';
import { useBruteForceProtection } from '../hooks/useBruteForceProtection';
import { useEncryptionKeys } from '../hooks/useEncryptionKeys';
import { useIdleTimeout } from '../hooks/useIdleTimeout';
import { useMfa } from '../hooks/useMfa';
import {
  logoutUser as apiLogoutUser,
  logLogin,
  registerLogoutCallback,
  resetLogoutFlag,
} from '../services/api';
import { auth } from '../services/firebase';
import {
  type BruteForceProtectionStatus,
  checkBruteForceProtection,
  formatTimeRemaining,
  handleSuccessfulLogin,
  recordFailedAttempt,
} from '../utils/bruteForceProtection';

interface AuthContextType {
  user: FirebaseUser | null;
  idToken: string | null;
  loginWithEmail: (email: string, password: string) => Promise<void>;
  signUpWithEmail: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  sendPasswordResetEmail: (email: string) => Promise<void>;
  isAuthenticated: boolean;
  isLoading: boolean;
  isLoggingIn: boolean;
  isAuthLoading?: boolean;
  isLoggingOut: boolean;
  authError: string | null;
  clearAuthError: () => void;
  bruteForceStatus: BruteForceProtectionStatus | null;
  isLoginBlocked: boolean;
  loginDelay: number;
  userIP: string | null;
  delayMessage: string | null;
  isLoggedOut: boolean;
  encryptionKey: CryptoKey | null;
  recoveryCode: string | null;
  clearRecoveryCode: () => void;
  isRecoveryNeeded: boolean;
  requireEncryptionRecovery: () => void;
  recoverEncryption: (recoveryCode: string, password: string) => Promise<void>;
  resetEncryptionKeys: (password: string) => Promise<void>;
  loginWithMicrosoft: () => Promise<void>;
  isLinkingMode: boolean;
  linkingEmail: string;
  cancelLinking: () => void;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthContext = createContext<AuthContextType | null>(null);

let isLogoutInProgress = false;

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [isLoggingIn, setIsLoggingIn] = useState<boolean>(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [isLoggedOut, setIsLoggedOut] = useState<boolean>(false);

  const {
    isLinkingMode,
    setIsLinkingMode,
    linkingEmail,
    setLinkingEmail,
    pendingCred,
    setPendingCred,
    cancelLinking,
  } = useAccountLinking(setAuthError);

  const {
    encryptionKey,
    recoveryCode,
    clearRecoveryCode,
    isRecoveryNeeded,
    requireEncryptionRecovery,
    recoverEncryption,
    resetEncryptionKeys,
    handleEncryptionSetup,
    clearEncryptionState,
    tempPassword,
    setTempPassword,
  } = useEncryptionKeys(setAuthError);

  const { mfaResolver, isMfaChallengeVisible, startMfaSignIn, completeMfaSignIn, cancelMfaSignIn } =
    useMfa({
      setIsLoggingIn,
      setAuthError,
      isLinkingMode,
      pendingCred,
    });

  const {
    bruteForceStatus,
    setBruteForceStatus,
    isLoginBlocked,
    setIsLoginBlocked,
    loginDelay,
    setLoginDelay,
    userIP,
    delayMessage,
    setDelayMessage,
    delayTimeoutId,
    setDelayTimeoutId,
  } = useBruteForceProtection();

  const logoutUser = useCallback(async (): Promise<void> => {
    if (isLogoutInProgress) {
      return;
    }

    if (!auth.currentUser) {
      resetLogoutFlag();
      return;
    }

    isLogoutInProgress = true;
    setIsLoggingOut(true);

    try {
      clearEncryptionState();
      if (auth.currentUser) {
        apiLogoutUser().catch(() => {
          // Ignore logout errors
        });
      }
      await signOut(auth);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setAuthError(errorMessage);
    } finally {
      isLogoutInProgress = false;
      setIsLoggingOut(false);
      resetLogoutFlag();
    }
  }, [clearEncryptionState]);

  useIdleTimeout({ user, logoutUser });

  useEffect(() => {
    registerLogoutCallback(logoutUser);
    return () => registerLogoutCallback(null);
  }, [logoutUser]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
      if (!firebaseUser) {
        setUser(null);
        setIdToken(null);
        setIsLoggedOut(true);
        setLoading(false);
        setIsLoggingOut(false);
        setIsLoggingIn(false);
        return;
      }

      setAuthError(null);
      isLogoutInProgress = false;

      try {
        const token = await getIdToken(firebaseUser);
        setIdToken(token);
        resetLogoutFlag();
        setIsLoggedOut(false);
        setUser(firebaseUser);
      } catch (error: unknown) {
        setUser(null);
        setIdToken(null);
        clearEncryptionState();
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'An unexpected error occurred during authentication.';
        setAuthError(errorMessage);
      } finally {
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, [clearEncryptionState]);

  const loginWithEmail = async (email: string, password: string): Promise<void> => {
    setAuthError(null);
    setDelayMessage(null);
    setBruteForceStatus(null);
    setIsLoginBlocked(false);
    setIsLoggingIn(true);

    if (!email) {
      setAuthError('Email address is required');
      setIsLoggingIn(false);
      return;
    }

    try {
      const protection = checkBruteForceProtection(email, userIP);
      setBruteForceStatus(protection);

      if (protection.isBlocked) {
        setIsLoginBlocked(true);

        const timeRemaining = Math.max(
          protection.accountLockoutTimeRemaining,
          protection.ipLockoutTimeRemaining
        );

        let blockMessage = 'Too many failed login attempts. ';
        if (protection.isAccountLocked && protection.isIpLocked) {
          blockMessage += `Account and IP are temporarily locked. Try again in ${formatTimeRemaining(timeRemaining)}.`;
        } else if (protection.isAccountLocked) {
          blockMessage += `Account is temporarily locked. Try again in ${formatTimeRemaining(protection.accountLockoutTimeRemaining)}.`;
        } else if (protection.isIpLocked) {
          blockMessage += `IP address is temporarily locked due to suspicious activity. Try again in ${formatTimeRemaining(protection.ipLockoutTimeRemaining)}.`;
        }

        setAuthError(blockMessage);
        setIsLoggingIn(false);
        return;
      }

      const userCredential = await signInWithEmailAndPassword(auth, email, password);

      if (isLinkingMode && pendingCred) {
        await linkWithCredential(userCredential.user, pendingCred);
      }

      await handleEncryptionSetup(password);

      logLogin().catch(() => {
        /* ignore */
      });

      handleSuccessfulLogin(email, userIP);
      setBruteForceStatus(null);
      setDelayMessage(null);
      setIsLoginBlocked(false);
      setLoginDelay(0);

      if (delayTimeoutId) {
        clearTimeout(delayTimeoutId);
        setDelayTimeoutId(null);
      }

      setIsLoggingIn(false);
    } catch (error: unknown) {
      const authError = error as AuthError;
      if (authError.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, authError as MultiFactorError);
        setTempPassword(password);
        await startMfaSignIn(resolver);
        return;
      }

      if (email) {
        recordFailedAttempt(email, false);
        if (userIP) {
          recordFailedAttempt(userIP, true);
        }

        const updatedProtection = checkBruteForceProtection(email, userIP);
        setBruteForceStatus(updatedProtection);

        if (updatedProtection.progressiveDelay > 0) {
          setLoginDelay(updatedProtection.progressiveDelay);
          setDelayMessage(
            `Please wait ${Math.ceil(updatedProtection.progressiveDelay / 1000)} seconds before trying again...`
          );

          if (delayTimeoutId) {
            clearTimeout(delayTimeoutId);
          }

          const timeoutId = setTimeout(() => {
            setLoginDelay(0);
            setDelayMessage(null);
            setDelayTimeoutId(null);
          }, updatedProtection.progressiveDelay);

          setDelayTimeoutId(timeoutId);
        }

        if (!updatedProtection.isBlocked && updatedProtection.attemptsRemaining > 0) {
          setAuthError(
            `Incorrect email or password. ${updatedProtection.attemptsRemaining} attempts remaining before account lockout.`
          );
        } else if (updatedProtection.isBlocked) {
          const timeRemaining = Math.max(
            updatedProtection.accountLockoutTimeRemaining,
            updatedProtection.ipLockoutTimeRemaining
          );
          setAuthError(
            `Account locked due to too many failed attempts. Try again in ${formatTimeRemaining(timeRemaining)}.`
          );
          setIsLoginBlocked(true);
        } else {
          setAuthError('Incorrect email or password.');
        }
      } else {
        setAuthError('Incorrect email or password.');
      }

      setIsLoggingIn(false);
    }
  };

  const signUpWithEmail = async (email: string, password: string): Promise<void> => {
    setAuthError(null);
    try {
      await createUserWithEmailAndPassword(auth, email, password);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setAuthError(errorMessage);
    }
  };

  const loginWithMicrosoft = async (): Promise<void> => {
    setIsLoggingIn(true);
    setAuthError(null);

    try {
      const provider = new OAuthProvider('microsoft.com');
      provider.setCustomParameters({
        prompt: 'select_account',
      });
      await signInWithPopup(auth, provider);
    } catch (error: unknown) {
      const err = error as AuthError;

      if (err.code === 'auth/multi-factor-auth-required') {
        const resolver = getMultiFactorResolver(auth, err as MultiFactorError);
        setTempPassword(null);
        await startMfaSignIn(resolver);
        return;
      }

      if (err.code === 'auth/account-exists-with-different-credential') {
        const credential = OAuthProvider.credentialFromError(err);
        const attemptedEmail = err.customData?.email || '';

        if (credential) {
          setPendingCred(credential);
          setLinkingEmail(attemptedEmail);
          setIsLinkingMode(true);
          setAuthError(null);
          setIsLoggingIn(false);
          return;
        }
      }

      if (err.code === 'auth/popup-closed-by-user') {
        setAuthError('The login popup was closed before finishing. Please try again.');
      } else if (err.code === 'auth/popup-blocked') {
        setAuthError('The login popup was blocked by your browser. Please allow popups.');
      } else {
        setAuthError(err.message || 'An error occurred during sign in.');
      }
      setIsLoggingIn(false);
    }
  };

  const handlePasswordReset = async (email: string): Promise<void> => {
    setAuthError(null);
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error);
      setAuthError(errorMessage);
      throw error;
    }
  };

  const value: AuthContextType = {
    user,
    idToken,
    loginWithEmail,
    signUpWithEmail,
    logout: logoutUser,
    sendPasswordResetEmail: handlePasswordReset,
    isAuthenticated: !!user,
    isLoading: loading,
    isLoggingIn,
    isAuthLoading: loading,
    isLoggingOut,
    authError,
    clearAuthError: () => setAuthError(null),
    bruteForceStatus,
    isLoginBlocked,
    loginDelay,
    userIP,
    delayMessage,
    isLoggedOut,
    encryptionKey,
    recoveryCode,
    clearRecoveryCode,
    isRecoveryNeeded,
    requireEncryptionRecovery,
    recoverEncryption,
    resetEncryptionKeys,
    loginWithMicrosoft,
    isLinkingMode,
    linkingEmail,
    cancelLinking,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <MfaChallengeDialog
        open={isMfaChallengeVisible}
        resolver={mfaResolver}
        onComplete={async (code) => {
          await completeMfaSignIn(code);
          if (tempPassword) {
            await handleEncryptionSetup(tempPassword);
            setTempPassword(null);
          }
        }}
        onCancel={() => {
          cancelMfaSignIn();
          setTempPassword(null);
        }}
      />
      <div id="recaptcha-container-login" />
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
